import React from 'react';
import { motion } from 'framer-motion';
import { Compass, Flag, Heart, Sparkles, Users } from 'lucide-react';

/**
 * VisionMission — Twin-pillar Vision & Mission band + "The BITZ Promise" tri-card grid.
 *
 * Aesthetic: black-gold, international standard, editorial spacing.
 * Positioning: premium lifestyle + smart everyday savings (daily-needs ecosystem).
 */

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

const PROMISES = [
  {
    icon: Sparkles,
    title: 'Premium Access',
    body:
      'Curated stays, dining and wellness through our partner network — opened up to members the way a well-connected friend would.',
    accent: '#D4AF37',
  },
  {
    icon: Heart,
    title: 'Smart Everyday',
    body:
      'A trusted daily-needs ecosystem of vetted partners that makes the small things — laundry, repairs, provisions, care — feel effortless.',
    accent: '#D4AF37',
  },
  {
    icon: Users,
    title: 'Exclusive Belonging',
    body:
      'A community of like-minded members, families and corporates connected through one card and a growing world of affiliations.',
    accent: '#D4AF37',
  },
];

const VisionMission = () => {
  return (
    <section
      id="vision-mission"
      className="relative py-20 sm:py-28 lg:py-32 bg-gradient-to-b from-[#0A0A0B] via-[#0D0F1A] to-[#0A0A0B] overflow-hidden"
      data-testid="vision-mission"
    >
      {/* Background ornament */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            'radial-gradient(circle at 20% 20%, #D4AF37 1px, transparent 1px), radial-gradient(circle at 80% 80%, #D4AF37 1px, transparent 1px)',
          backgroundSize: '90px 90px, 130px 130px',
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        {/* Eyebrow + headline */}
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-60px' }}
          variants={fadeUp}
          className="text-center mb-14 lg:mb-20 max-w-3xl mx-auto"
        >
          <div className="inline-flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-[#D4AF37]" />
            <span
              className="text-[10px] sm:text-xs tracking-[0.4em] uppercase text-[#D4AF37] font-medium"
              style={{ fontFamily: 'Cinzel, Playfair Display, serif' }}
            >
              Vision · Mission · Promise
            </span>
            <span className="h-px w-10 bg-[#D4AF37]" />
          </div>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-[1.1] tracking-tight mb-5"
            style={{ fontFamily: 'Playfair Display, Cinzel, serif' }}
          >
            Why{' '}
            <span className="bg-gradient-to-r from-[#F5D77A] via-[#D4AF37] to-[#A67C00] bg-clip-text text-transparent">
              BITZ exists.
            </span>
          </h2>
          <p
            className="text-base sm:text-lg text-gray-300/85 leading-relaxed"
            style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
          >
            A clear point of view on how a modern lifestyle access club should feel —
            elevated, organised, partner-led and quietly generous to the people inside it.
          </p>
        </motion.div>

        {/* Twin pillars: Vision | Mission */}
        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 mb-20 lg:mb-28">
          {/* VISION */}
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: '-40px' }}
            variants={fadeUp}
            className="relative group"
            data-testid="vision-card"
          >
            <div className="absolute -inset-px bg-gradient-to-br from-[#D4AF37]/25 via-transparent to-[#D4AF37]/10 rounded-3xl opacity-60 group-hover:opacity-100 transition-opacity duration-700 blur-sm pointer-events-none" />
            <div className="relative h-full bg-gradient-to-br from-[#101012] to-[#15151A] border border-[#D4AF37]/15 rounded-3xl p-8 sm:p-10 lg:p-12 overflow-hidden">
              <div
                className="absolute -top-24 -right-24 w-64 h-64 rounded-full opacity-10 blur-3xl pointer-events-none"
                style={{ background: '#D4AF37' }}
              />
              <div className="relative">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center">
                    <Compass className="w-7 h-7 text-[#D4AF37]" />
                  </div>
                  <div>
                    <p
                      className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37]"
                      style={{ fontFamily: 'Cinzel, serif' }}
                    >
                      Our Vision
                    </p>
                    <h3
                      className="text-2xl sm:text-3xl font-bold text-white mt-1"
                      style={{ fontFamily: 'Playfair Display, serif' }}
                    >
                      A new standard of belonging.
                    </h3>
                  </div>
                </div>
                <div className="h-px w-16 bg-[#D4AF37]/40 mb-6" />
                <p className="text-gray-300/90 text-base sm:text-lg leading-relaxed">
                  To become the most respected lifestyle access club for families,
                  professionals and corporates — a growing international network where
                  premium experiences and thoughtful everyday support are unlocked
                  through one trusted membership, wherever life takes our members next.
                </p>
              </div>
            </div>
          </motion.div>

          {/* MISSION */}
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: '-40px' }}
            variants={fadeUp}
            custom={1}
            className="relative group"
            data-testid="mission-card"
          >
            <div className="absolute -inset-px bg-gradient-to-br from-[#D4AF37]/25 via-transparent to-[#D4AF37]/10 rounded-3xl opacity-60 group-hover:opacity-100 transition-opacity duration-700 blur-sm pointer-events-none" />
            <div className="relative h-full bg-gradient-to-br from-[#101012] to-[#15151A] border border-[#D4AF37]/15 rounded-3xl p-8 sm:p-10 lg:p-12 overflow-hidden">
              <div
                className="absolute -bottom-24 -left-24 w-64 h-64 rounded-full opacity-10 blur-3xl pointer-events-none"
                style={{ background: '#D4AF37' }}
              />
              <div className="relative">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center">
                    <Flag className="w-7 h-7 text-[#D4AF37]" />
                  </div>
                  <div>
                    <p
                      className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37]"
                      style={{ fontFamily: 'Cinzel, serif' }}
                    >
                      Our Mission
                    </p>
                    <h3
                      className="text-2xl sm:text-3xl font-bold text-white mt-1"
                      style={{ fontFamily: 'Playfair Display, serif' }}
                    >
                      Make premium feel effortless.
                    </h3>
                  </div>
                </div>
                <div className="h-px w-16 bg-[#D4AF37]/40 mb-6" />
                <p className="text-gray-300/90 text-base sm:text-lg leading-relaxed">
                  To build and grow a curated network of hospitality, wellness and
                  daily-life partners — and bring it together in one membership so being
                  a BITZ member consistently feels like a smarter, calmer, more elegant
                  way to live and travel.
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* The BITZ Promise — 3-card grid */}
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          variants={fadeUp}
          className="text-center mb-12 lg:mb-14"
        >
          <p
            className="text-[10px] sm:text-xs tracking-[0.4em] uppercase text-[#D4AF37] mb-3"
            style={{ fontFamily: 'Cinzel, serif' }}
          >
            The BITZ Promise
          </p>
          <h3
            className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Three things every member can count on.
          </h3>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
          {PROMISES.map((p, i) => {
            const Ico = p.icon;
            return (
              <motion.div
                key={p.title}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: '-40px' }}
                custom={i}
                variants={fadeUp}
                className="relative group bg-[#0F0F10] border border-white/5 hover:border-[#D4AF37]/40 rounded-2xl p-7 sm:p-8 transition-all duration-500 hover:translate-y-[-4px] hover:shadow-[0_24px_60px_-20px_rgba(212,175,55,0.2)] overflow-hidden"
                data-testid={`promise-card-${i}`}
              >
                <div
                  className="absolute -top-16 -right-16 w-48 h-48 rounded-full opacity-10 group-hover:opacity-25 blur-3xl transition-opacity duration-700 pointer-events-none"
                  style={{ background: p.accent }}
                />
                <div className="relative">
                  <div
                    className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center mb-5 border"
                    style={{
                      background: `${p.accent}15`,
                      borderColor: `${p.accent}40`,
                    }}
                  >
                    <Ico className="w-6 h-6 sm:w-7 sm:h-7" style={{ color: p.accent }} />
                  </div>
                  <h4
                    className="text-xl sm:text-2xl font-bold text-white mb-3"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    {p.title}
                  </h4>
                  <p className="text-sm sm:text-base text-gray-400 leading-relaxed">{p.body}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default VisionMission;
